import type { DayException } from '../engine/types'

/** The kinds as people say them — plan §7 step 2: the kinds suppress
    identically in the engine but read differently to a human. */
export const EXCEPTION_LABEL: Record<DayException['kind'], string> = {
  LEAVE: 'Leave',
  UNAVAILABLE: 'Unavailable',
  NOT_WORKING: 'Not working',
}

export interface ExceptionDayRowProps {
  /** Already formatted for display, e.g. 'Tue 22 Sep'. */
  date: string
  kind: DayException['kind']
  /** The day's free-text note, shown when one exists. */
  note?: string
}

/**
 * One exception day as a row, the agenda counterpart to `RosterShiftRow` —
 * a separate component rather than a variant of it, deliberately: the two
 * share no props (`code`/`timeRange` have no meaning on a day with no
 * shift) and keeping them apart is what makes it impossible for an
 * exception day to render as a shift. The date sits where the shift row's
 * date sits; the kind stands on the chip's side of the row as a plain
 * labelled badge, never a `ShiftCodeChip`.
 *
 * `date` arrives pre-formatted, the same contract `RosterShiftRow` runs —
 * date maths stays with the caller, this component only lays words out.
 *
 * Read-only like every row: no handler, no `<button>`. Task 8's
 * `CalendarAgenda` consumes this — it renders one row per exception,
 * interleaved with `RosterShiftRow`s in date order.
 */
export function ExceptionDayRow({ date, kind, note }: ExceptionDayRowProps) {
  return (
    <div className="ss-exception-row" data-kind={kind}>
      <span className="ss-exception-row__when">
        <span className="ss-exception-row__date">{date}</span>
        {note ? <span className="ss-exception-row__note">{note}</span> : null}
      </span>
      <span className="ss-exception-row__kind" data-kind={kind}>
        {EXCEPTION_LABEL[kind]}
      </span>
    </div>
  )
}
